/**
 * LineChart — minimal area/line chart in pure SVG.
 *
 * Why not Recharts: the rest of the app has zero chart dependencies and the
 * data we render is tiny (≤ 365 buckets). A 200-line component avoids the
 * 200kb gzipped weight of the obvious library options and lets us match the
 * design system tokens directly.
 *
 * Responsive: the SVG uses `viewBox` + `preserveAspectRatio` so the parent's
 * width controls the size. The container should set a fixed height.
 */

import * as React from 'react'
import type { TimeSeriesPoint } from '../types'

interface Props {
  data: TimeSeriesPoint[]
  height?: number
  /** Tailwind-compatible stroke / fill via CSS vars on `.text-primary`. */
  accentClassName?: string
  /** Format a Y value for the axis + tooltip. Defaults to localised integers. */
  formatValue?: (value: number) => string
  /** Format a bucket key for the X axis labels. Defaults to `MMM d` / `MMM`. */
  formatLabel?: (bucket: string) => string
  /** Render an "area" fill under the line. */
  area?: boolean
  ariaLabel?: string
}

const PADDING = { top: 16, right: 16, bottom: 28, left: 44 }

export function LineChart({
  data,
  height = 220,
  accentClassName = 'text-primary',
  formatValue,
  formatLabel,
  area = true,
  ariaLabel = 'Trend line chart',
}: Props) {
  const width = 600 // viewBox width — preserves aspect on resize
  const fmtV = formatValue ?? defaultValueFormatter
  const fmtL = formatLabel ?? defaultLabelFormatter

  const max = Math.max(1, ...data.map((p) => p.value))
  // Pad the y-axis upper bound to the next pretty number so labels don't clip.
  const yMax = niceCeil(max)

  const chartW = width - PADDING.left - PADDING.right
  const chartH = height - PADDING.top - PADDING.bottom

  // X spacing: one column per data point centred between left/right pad.
  const stepX = data.length > 1 ? chartW / (data.length - 1) : 0
  const x = (i: number) => PADDING.left + i * stepX
  const y = (v: number) => PADDING.top + chartH - (v / yMax) * chartH

  const linePath = data
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(2)} ${y(p.value).toFixed(2)}`)
    .join(' ')

  const areaPath =
    data.length > 0
      ? `${linePath} L ${x(data.length - 1).toFixed(2)} ${(PADDING.top + chartH).toFixed(2)} L ${x(0).toFixed(2)} ${(PADDING.top + chartH).toFixed(2)} Z`
      : ''

  // Pretty 4-tick Y axis.
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((t) => ({
    y: PADDING.top + chartH - t * chartH,
    label: fmtV(Math.round(yMax * t)),
  }))

  // Pick at most 6 evenly-spaced X labels so they never overlap.
  const labelStride = Math.max(1, Math.ceil(data.length / 6))
  const xLabels = data
    .map((p, i) => ({ i, p }))
    .filter(({ i }) => i % labelStride === 0 || i === data.length - 1)

  if (data.length === 0) {
    return (
      <div
        className="w-full flex items-center justify-center text-on-surface-variant text-sm"
        style={{ height }}
      >
        No data yet for this range.
      </div>
    )
  }

  return (
    <svg
      role="img"
      aria-label={ariaLabel}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className="w-full block"
      style={{ height }}
    >
      {/* Grid lines + Y labels */}
      {yTicks.map((t, idx) => (
        <g key={idx}>
          <line
            x1={PADDING.left}
            x2={width - PADDING.right}
            y1={t.y}
            y2={t.y}
            stroke="currentColor"
            strokeOpacity={0.08}
            strokeDasharray={idx === 0 ? '0' : '3 3'}
            className="text-on-surface"
          />
          <text
            x={PADDING.left - 8}
            y={t.y + 3}
            textAnchor="end"
            fontSize={10}
            className="fill-on-surface-variant"
          >
            {t.label}
          </text>
        </g>
      ))}

      {/* Area + line */}
      {area && (
        <path
          d={areaPath}
          className={accentClassName}
          fill="currentColor"
          opacity={0.12}
        />
      )}
      <path
        d={linePath}
        className={accentClassName}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Data point dots */}
      {data.map((p, i) => (
        <circle
          key={i}
          cx={x(i)}
          cy={y(p.value)}
          r={2.5}
          className={accentClassName}
          fill="currentColor"
        >
          <title>{`${fmtL(p.bucket)}: ${fmtV(p.value)}`}</title>
        </circle>
      ))}

      {/* X labels */}
      {xLabels.map(({ i, p }) => (
        <text
          key={i}
          x={x(i)}
          y={height - 8}
          textAnchor="middle"
          fontSize={10}
          className="fill-on-surface-variant"
        >
          {fmtL(p.bucket)}
        </text>
      ))}
    </svg>
  )
}

function niceCeil(v: number): number {
  if (v <= 1) return 1
  const pow = Math.pow(10, Math.floor(Math.log10(v)))
  const norm = v / pow
  const steps = [1, 2, 2.5, 5, 10]
  const step = steps.find((s) => norm <= s) ?? 10
  return step * pow
}

function defaultValueFormatter(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`
  return v.toLocaleString()
}

function defaultLabelFormatter(bucket: string): string {
  // bucket is either YYYY-MM-DD (day) or YYYY-MM (month).
  if (bucket.length === 7) {
    const d = new Date(`${bucket}-01T00:00:00Z`)
    return d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' })
  }
  const d = new Date(`${bucket}T00:00:00Z`)
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}
